import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { LayoutDashboard, Users, Bus, ClipboardList, LogOut, AlertCircle, CalendarDays, UserCog, QrCode, KeyRound, Menu, X } from "lucide-react";
import { toast } from "sonner";

const nav = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
    { to: "/admin/students", label: "Students", icon: Users, testid: "nav-students" },
    { to: "/admin/buses", label: "Buses & Routes", icon: Bus, testid: "nav-buses" },
    { to: "/admin/operators", label: "Operators", icon: UserCog, testid: "nav-operators" },
    { to: "/admin/qrcodes", label: "QR Codes", icon: QrCode, testid: "nav-qrcodes" },
    { to: "/admin/cycles", label: "Payment Cycles", icon: CalendarDays, testid: "nav-cycles" },
    { to: "/admin/attendance", label: "Attendance Logs", icon: ClipboardList, testid: "nav-attendance" },
    { to: "/admin/payments", label: "Payment Report", icon: AlertCircle, testid: "nav-payments" },
];

export default function AdminLayout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        toast.success("Signed out");
        navigate("/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] lg:flex">
            {/* Mobile top bar — hidden on desktop, where the sidebar is always visible */}
            <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-[#E5E5E5] flex items-center justify-between px-4 h-16">
                <Logo size="sm" />
                <button
                    onClick={() => setSidebarOpen(true)}
                    data-testid="open-sidebar"
                    aria-label="Open menu"
                    className="p-2 hover:bg-[#F8F9FA]"
                >
                    <Menu size={22} />
                </button>
            </header>

            {/* Backdrop — mobile only, closes the drawer on tap */}
            {sidebarOpen && (
                <div
                    onClick={() => setSidebarOpen(false)}
                    className="lg:hidden fixed inset-0 bg-black/50 z-40"
                    data-testid="sidebar-backdrop"
                />
            )}

            <aside
                className={`w-64 bg-white border-r border-[#E5E5E5] flex flex-col fixed lg:sticky top-0 h-screen h-[100dvh] z-50 transition-transform duration-200 ease-out ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full"
                } lg:translate-x-0`}
            >
                <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
                    <div>
                        <Logo size="md" />
                        <p className="label-caps mt-2">Admin Console</p>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        data-testid="close-sidebar"
                        aria-label="Close menu"
                        className="lg:hidden p-1 hover:bg-[#F8F9FA] -mr-2"
                    >
                        <X size={20} />
                    </button>
                </div>
                <nav className="flex-1 py-4 overflow-y-auto">
                    {nav.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={item.testid}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-4 transition-colors ${
                                        isActive
                                            ? "border-[#FF3B30] bg-[#FF3B30]/5 text-[#111111]"
                                            : "border-transparent text-[#666666] hover:text-[#111111] hover:bg-[#F8F9FA]"
                                    }`
                                }
                            >
                                <Icon size={18} />
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="p-4 pb-6 border-t border-[#E5E5E5]">
                    <div className="mb-3">
                        <p className="text-xs text-[#666666]">Signed in as</p>
                        <p className="font-bold text-sm text-[#111111]" data-testid="current-user-name">
                            {user?.name}
                        </p>
                        <p className="text-xs text-[#666666] font-mono">{user?.email}</p>
                    </div>
                    <button
                        onClick={() => setChangePasswordOpen(true)}
                        data-testid="change-password-button"
                        className="w-full flex items-center justify-center gap-2 border border-[#111111] px-4 py-2 text-sm font-bold hover:bg-[#111111] hover:text-white transition-colors mb-2"
                    >
                        <KeyRound size={14} />
                        Change Password
                    </button>
                    <button
                        onClick={handleLogout}
                        data-testid="logout-button"
                        className="w-full flex items-center justify-center gap-2 border border-[#111111] px-4 py-2 text-sm font-bold hover:bg-[#111111] hover:text-white transition-colors"
                    >
                        <LogOut size={14} />
                        Sign Out
                    </button>
                </div>
            </aside>
            <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">{children}</main>
            {changePasswordOpen && <ChangePasswordModal onClose={() => setChangePasswordOpen(false)} />}
        </div>
    );
}
